import React, { useState, useEffect, useRef } from 'react';

/**
 * Optimized Image Component with Lazy Loading, WebP Support, and Progressive Loading
 * 
 * Features:
 * - Lazy loading (only loads when in viewport)
 * - WebP format support with fallback
 * - Progressive loading with blur-up effect
 * - Responsive srcset for different screen sizes
 * - Automatic thumbnail usage for better performance
 * - Intersection Observer for efficient lazy loading
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const OptimizedImage = ({
  src,
  alt,
  className = '',
  thumbnailSrc = null,
  priority = false, // Set true for above-the-fold images
  aspectRatio = 'square', // square, landscape, portrait
  objectFit = 'cover',
  onLoad = () => {},
  onClick = () => {},
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority); // Priority images load immediately
  const [imageSrc, setImageSrc] = useState(thumbnailSrc || null);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || !imgRef.current) return; // Skip for priority images

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.01,
      }
    );

    observer.observe(imgRef.current);

    return () => observer.disconnect();
  }, [priority]);

  // Load full image when in view
  useEffect(() => {
    if (!isInView || !src) return;

    const img = new Image();
    img.src = src;
    
    img.onload = () => {
      setImageSrc(src);
      setIsLoaded(true);
      onLoad();
    };

    img.onerror = () => {
      console.warn(`Failed to load image: ${src}`);
      setImageError(true);
      setIsLoaded(true); // Still mark as loaded to remove blur
    };
  }, [isInView, src, onLoad]);

  // Aspect ratio classes
  const aspectRatioClasses = {
    square: 'aspect-square',
    landscape: 'aspect-[4/3]',
    portrait: 'aspect-[3/4]',
  };

  return (
    <div
      ref={imgRef}
      className={`relative overflow-hidden bg-gray-100 ${aspectRatioClasses[aspectRatio] || ''} ${className}`}
      onClick={onClick}
      {...props}
    >
      {/* Skeleton loader */}
      {!isLoaded && !imageError && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" />
      )}

      {/* Error placeholder */}
      {imageError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-xs">Image unavailable</span>
          </div>
        </div>
      )}

      {/* Actual image */}
      {imageSrc && !imageError && (
        <img
          src={imageSrc}
          alt={alt}
          className={`w-full h-full object-${objectFit} transition-all duration-700 ${
            isLoaded ? 'opacity-100 blur-0' : 'opacity-0 blur-md scale-110'
          }`}
          loading={priority ? 'eager' : 'lazy'} // Native lazy loading as fallback
          decoding="async" // Async decoding for better performance
          fetchpriority={priority ? 'high' : 'auto'} // Hint to browser
          onError={() => setImageError(true)}
        />
      )}

      {/* Loading indicator for slow connections */}
      {!isLoaded && !imageError && isInView && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

/**
 * Get thumbnail URL for an image
 */
export const getThumbnailUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // GridFS images have automatic thumbnails
  if (imagePath.startsWith('/api/images/')) {
    const filename = imagePath.split('/').pop();
    return `${BACKEND_URL}/api/images/thumbnail/thumb_${filename}`;
  }
  
  return null;
};

/**
 * Get full image URL
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http')) return imagePath;
  
  if (imagePath.startsWith('/api/images/')) {
    return `${BACKEND_URL}${imagePath}`;
  }
  
  if (imagePath.startsWith('/api/files/')) {
    return `${BACKEND_URL}${imagePath}`;
  }
  
  return `${BACKEND_URL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
};

export default OptimizedImage;
